import React, { useState } from 'react';
import { Table, Dropdown, Icon, Menu } from 'antd';
import PropTypes from 'prop-types';
import { EditableCell, EditableFormRow } from '../EditableCell';
import sortDefault from '../../Images/Icons/sort-default.svg';
import sortAscending from '../../Images/Icons/sort-ascending.svg';
import sortDescending from '../../Images/Icons/sort-descending.svg';
import horizontalEllipsis from '../../Images/Icons/horizontalEllipsis.svg';
import placeholderImage from '../../Images/Icons/placeholder.png';
import ImportExportLoader from '../Loader/ImportExportLoader';
import 'antd/dist/antd.css';
import './Table.css';
import { isEmpty } from '../../Utils/validationUtil';

function TableView({
  modelExport,
  runtimeExport,
  fetchRecords,
  addOrEditRecord,
  onRecordClick,
  deleteRecord,
  cloneRecord,
  exportModel,
  data,
  totalRecords,
  title,
  columnNames,
  activeRecord,
  classNames,
  tableType,
  currentPage,
  permissions,
  exportLoader,
  showRedundantViews,
  runtime,
  clientHeight,
}) {
  const [record, setRecord] = useState({});
  const [recordId, setRecordId] = useState('');
  const [fetchParams, setFetchParams] = useState({
    sortBy: '',
    sortDirection: '',
    offset: 0,
    size: 15,
  });

  const releaseToRepo = (model) => {
    modelExport(model);
  };

  const exportRuntime = (id) => {
    runtimeExport(id);
  };

  const onDropdownClick = (id) => {
    if (recordId) {
      setRecordId(recordId !== id ? id : '');
    } else {
      setRecordId(id);
    }
  };

  const onPageChange = (currentPageVal) => {
    const offset = currentPageVal - 1;
    const { sortBy, sortDirection } = fetchParams;
    setFetchParams((prevfetchParams) => ({
      ...prevfetchParams,
      offset,
    }));
    fetchRecords(offset, sortBy, sortDirection);
  };

  const addOrEdit = (payload, enter) => {
    addOrEditRecord(payload);
    setRecordId('');
    setRecord(enter ? {} : record);
  };

  const handleTableChange = (column, sortDirection) => {
    const { offset } = fetchParams;
    const isDefault = sortDirection === 'default';
    setFetchParams((prevfetchParams) => ({
      ...prevfetchParams,
      sortBy: isDefault ? '' : column,
      sortDirection: isDefault ? '' : sortDirection,
    }));
    fetchRecords(offset, column, isDefault ? '' : sortDirection);
  };

  const makeEditable = (currentRecord) => {
    setRecord(currentRecord);
  };

  const onDeleteRecord = (currentRecord) => {
    deleteRecord(currentRecord);
    setRecordId('');
  };

  const onCloneRecord = (currentRecord) => {
    cloneRecord(currentRecord);
    setRecordId('');
  };

  const exportRecord = (currentRecord) => {
    exportModel(currentRecord.modelId);
    setRecordId('');
  };

  const paginationObj = {
    total: totalRecords,
    pageSize: 7,
    className: 'pagination',
    onChange: (updatedCurrentPage) => {
      onPageChange(updatedCurrentPage);
    },
    itemRender: (current, type, elem) => {
      if (type === 'prev') {
        return <span className="pageNavigate flaticon-back" />;
      }
      if (type === 'next') {
        return <span className="pageNavigate flaticon-arrow-right" />;
      }
      return elem;
    },
    defaultCurrent: currentPage + 1,
  };

  const moreOptions = (currentRecord, dropDownList) => {
    return (
      <Menu className="tableDropdown">
        {permissions.edit && (
          <Menu.Item
            key="0"
            onClick={(e) => {
              e.domEvent.stopPropagation();
              makeEditable(currentRecord);
            }}
          >
            {dropDownList.edit}
          </Menu.Item>
        )}
        {(permissions.delete || currentRecord.isOwner) && (
          <Menu.Item
            key="1"
            onClick={(e) => {
              e.domEvent.stopPropagation();
              onDeleteRecord(currentRecord);
            }}
          >
            {dropDownList.delete}
          </Menu.Item>
        )}
        {dropDownList.clone && permissions.clone && (
          <Menu.Item
            key="2"
            onClick={(e) => {
              e.domEvent.stopPropagation();
              onCloneRecord(currentRecord);
            }}
          >
            {dropDownList.clone}
          </Menu.Item>
        )}
        {permissions.archive && showRedundantViews && (
          <Menu.Item
            key="0"
            onClick={(e) => {
              e.domEvent.stopPropagation();
            }}
          >
            {dropDownList.archive}
          </Menu.Item>
        )}
        {dropDownList.exportModel && permissions.exportModel && (
          <Menu.Item
            key="2"
            onClick={(e) => {
              e.domEvent.stopPropagation();
              exportRecord(currentRecord);
            }}
          >
            {dropDownList.exportModel}
          </Menu.Item>
        )}
      </Menu>
    );
  };
  const sortOptions = (column) => {
    if (fetchParams.sortBy === column) {
      return fetchParams.sortDirection === 'ASC' ? (
        <Icon
          component={() => (
            <img
              src={sortAscending}
              className="sortIcon"
              alt="sortAscendingIcon"
            />
          )}
          onClick={() => handleTableChange(column, 'DESC')}
        />
      ) : (
        <Icon
          component={() => (
            <img
              src={sortDescending}
              className="sortIcon"
              alt="sortDefaultIcon"
            />
          )}
          onClick={() => handleTableChange('', 'default')}
        />
      );
    }
    return (
      <Icon
        component={() => (
          <img
            src={sortDefault}
            className="sortIcon"
            alt="sortDescendingIcon"
          />
        )}
        onClick={() => handleTableChange(column, 'ASC')}
      />
    );
  };
  const columns = [];
  if (columnNames.image) {
    columns.push({
      title: columnNames.image.title,
      key: columnNames.image.key,
      render: (currentRecord) => {
        let image = placeholderImage;
        const { thumbnailPath, imagePath } = currentRecord;
        if (thumbnailPath) {
          image = thumbnailPath;
        } else if (imagePath) {
          image = imagePath;
        }
        return (
          <div
            className="defaultWorkspaceImage"
            style={{
              background: `url("${image}")`,
            }}
          />
        );
      },
      width: columnNames.image.width,
      order: columnNames.image.order,
    });
  }
  if (columnNames.name) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.name.title}
          {sortOptions(columnNames.name.key)}
        </div>
      ),
      dataIndex: columnNames.name.dataIndex,
      key: columnNames.name.key,
      editable: columnNames.name.editable,
      width: columnNames.name.width,
      order: columnNames.name.order,
    });
  }
  if (columnNames.modelStatus) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.modelStatus.title}
          {sortOptions(columnNames.modelStatus.key)}
        </div>
      ),
      dataIndex: columnNames.modelStatus.dataIndex,
      key: columnNames.modelStatus.key,
      width: columnNames.modelStatus.width,
      order: columnNames.modelStatus.order,
      render: (currentRecord, recordObject) => (
        <div>
          {currentRecord}
          {recordObject &&
            recordObject.forgotPassword &&
            columnNames.modelStatus &&
            columnNames.modelStatus.icon}
        </div>
      ),
    });
  }
  if (columnNames.modelName) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.modelName.title}
          {sortOptions(columnNames.modelName.key)}
        </div>
      ),
      dataIndex: columnNames.modelName.dataIndex,
      key: columnNames.modelName.key,
      width: columnNames.modelName.width,
      order: columnNames.modelName.order,
    });
  }
  if (columnNames.role) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.role.title}
          {sortOptions(columnNames.role.key)}
        </div>
      ),
      dataIndex: columnNames.role.dataIndex,
      key: columnNames.role.key,
      width: columnNames.role.width,
      order: columnNames.role.order,
    });
  }
  if (columnNames.status) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.status.title}
          {sortOptions(columnNames.expiry.key)}
        </div>
      ),
      dataIndex: columnNames.status.dataIndex,
      key: columnNames.status.key,
      width: columnNames.status.width,
      order: columnNames.status.order,
      render: (currentRecord) => (
        <span>{currentRecord ? 'ACTIVE' : 'INACTIVE'}</span>
      ),
    });
  }
  if (columnNames.expiry) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.expiry.title}
          {sortOptions(columnNames.expiry.key)}
        </div>
      ),
      dataIndex: columnNames.expiry.dataIndex,
      key: columnNames.expiry.key,
      width: columnNames.expiry.width,
      order: columnNames.expiry.order,
      render: (currentRecord) => {
        const modified = new Date(currentRecord);
        return (
          <span>
            {`${modified.getDate()}- ${
              modified.getMonth() + 1
            } - ${modified.getFullYear()}`}
          </span>
        );
      },
    });
  }
  if (columnNames.activatedDate) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.activatedDate.title}
          {sortOptions(columnNames.activatedDate.key)}
        </div>
      ),
      dataIndex: columnNames.activatedDate.dataIndex,
      key: columnNames.activatedDate.key,
      width: columnNames.activatedDate.width,
      order: columnNames.activatedDate.order,
      render: (currentRecord) => {
        const modified = new Date(currentRecord);
        return (
          <span>
            {`${modified.getDate()}- ${
              modified.getMonth() + 1
            } - ${modified.getFullYear()}`}
          </span>
        );
      },
    });
  }
  if (columnNames.workspaceModifiedAt) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.workspaceModifiedAt.title}
          {sortOptions(columnNames.workspaceModifiedAt.key)}
        </div>
      ),
      dataIndex: columnNames.workspaceModifiedAt.dataIndex,
      key: columnNames.workspaceModifiedAt.key,
      width: columnNames.workspaceModifiedAt.width,
      order: columnNames.workspaceModifiedAt.order,
      render: (currentRecord) => {
        const modified = new Date(currentRecord);
        return (
          <span>
            {`${modified.getDate()}- ${
              modified.getMonth() + 1
            } - ${modified.getFullYear()}`}
          </span>
        );
      },
    });
  }
  if (columnNames.modelModifiedAt) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.modelModifiedAt.title}
          {sortOptions(columnNames.modelModifiedAt.key)}
        </div>
      ),
      dataIndex: columnNames.modelModifiedAt.dataIndex,
      key: columnNames.modelModifiedAt.key,
      width: columnNames.modelModifiedAt.width,
      order: columnNames.modelModifiedAt.order,
      render: (currentRecord) => {
        const modified = new Date(currentRecord);
        return (
          <span>
            {`${modified.getDate()} - ${
              modified.getMonth() + 1
            } - ${modified.getFullYear()}`}
          </span>
        );
      },
    });
  }
  if (columnNames.createdDate) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.createdDate.title}
          {sortOptions(columnNames.createdDate.key)}
        </div>
      ),
      dataIndex: columnNames.createdDate.dataIndex,
      key: columnNames.createdDate.key,
      width: columnNames.createdDate.width,
      order: columnNames.createdDate.order,
      render: (currentRecord) => {
        const modified = new Date(currentRecord);
        return (
          <span>
            {`${modified.getDate()} - ${
              modified.getMonth() + 1
            } - ${modified.getFullYear()}`}
          </span>
        );
      },
    });
  }
  if (columnNames.type) {
    columns.push({
      title: () => (
        <div className="columnHeader">
          {columnNames.type.title}
          {sortOptions(columnNames.type.key)}
        </div>
      ),
      dataIndex: columnNames.type.dataIndex,
      key: columnNames.type.key,
      width: columnNames.type.width,
      order: columnNames.type.order,
      render: (currentRecord) => <span>{currentRecord}</span>,
    });
  }
  if (
    permissions &&
    permissions.showActions &&
    columnNames.actions &&
    (runtime ? showRedundantViews : true)
  ) {
    columns.push({
      title: columnNames.actions.title,
      key: columnNames.actions.key,
      render: (currentRecord) => (
        <span className="exportText">
          {permissions && permissions.export && (
            <span
              onClick={(e) => {
                if (currentRecord.modelId && !currentRecord.isModelLocked) {
                  releaseToRepo(currentRecord);
                } else if (
                  !(
                    exportLoader &&
                    exportLoader.idList &&
                    exportLoader.idList.includes(currentRecord.inferenceId)
                  )
                ) {
                  exportRuntime(currentRecord.inferenceId);
                }
                e.stopPropagation();
              }}
              className="actionsContainer"
            >
              {() => {
                if (
                  exportLoader &&
                  exportLoader.idList &&
                  (exportLoader.idList.includes(currentRecord.inferenceId) ||
                    exportLoader.idList.includes(currentRecord.modelId))
                ) {
                  return <ImportExportLoader text="Exporting" />;
                }
                if (currentRecord.isModelLocked) {
                  return 'Model is locked';
                }
                return columnNames.actions.export;
              }}
            </span>
          )}
          {columnNames.actions.dropDownList && (
            <Dropdown
              overlay={() =>
                moreOptions(currentRecord, columnNames.actions.dropDownList)
              }
              placement="bottomRight"
              trigger={['click']}
              onVisibleChange={(visible) => {
                setRecordId(visible ? currentRecord.modelId : '');
              }}
              onClick={(e) => {
                onDropdownClick(currentRecord.modelId);
                e.stopPropagation();
              }}
              visible={
                recordId === currentRecord.modelId && !isEmpty(currentRecord)
              }
              className={`${currentRecord.isModelLocked && 'disabledOptions'}`}
            >
              <Icon
                component={() => (
                  <img
                    src={horizontalEllipsis}
                    className="ellipsisIcon"
                    alt="horizontalellipsisIcon"
                  />
                )}
                className="moreButton"
              />
            </Dropdown>
          )}
        </span>
      ),
      width: columnNames.actions.width,
      order: columnNames.actions.order,
    });
  }
  if (columnNames.emptyCells) {
    columns.push({
      title: () => <div className="columnHeader" />,
      key: columnNames.emptyCells.key,
      className: 'emptyCells',
      render: () => (
        <span
          style={{
            display: 'flex',
          }}
        />
      ),
      width: columnNames.emptyCells.width,
      order: columnNames.emptyCells.order,
    });
  }
  columns.sort((a, b) => a.order - b.order);
  const columnsT = columns.map((col) => {
    if (col && !col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (currentRecord) => ({
        currentRecord,
        editable: col ? col.editable : '',
        dataIndex: col ? col.dataIndex : '',
        title,
        handleSave: (payload, enter) => addOrEdit(payload, enter),
        // editing: currentRecord.modelId === currentRecord.modelId,
        nameClick: () => onRecordClick(currentRecord),
      }),
    };
  });
  const components = {
    body: {
      row: EditableFormRow,
      cell: EditableCell,
    },
  };

  return (
    <Table
      scroll={{
        y: clientHeight,
        scrollToFirstRowOnChange: true,
      }}
      className={`tableStyles ${
        !isEmpty(classNames) ? classNames.tableStyles : ''
      }`}
      rowClassName={(currentRecord) => {
        const activeId =
          activeRecord?.[`${tableType}Id`] ?? activeRecord?.[`${tableType}id`];
        const currentId =
          currentRecord[`${tableType}Id`] ?? currentRecord[`${tableType}id`];
        if (!isEmpty(activeRecord) && activeId === currentId) {
          return 'rowSelected';
        }
        return Object.prototype.hasOwnProperty.call(columnNames, 'emptyCells')
          ? 'rowWithEmptyCell'
          : '';
      }}
      columns={columnsT}
      components={components}
      dataSource={data}
      pagination={paginationObj}
      rowKey={(currentRecord) => currentRecord[`${tableType}Id`]}
      onRow={(currentRecord) => ({
        onClick: () => {
          if (isEmpty(record)) {
            onRecordClick(currentRecord);
          }
          if (!recordId) {
            setRecord({});
          } else if (!isEmpty(record)) {
            setRecordId('');
            setRecord({});
          }
        },
      })}
    />
  );
}

TableView.defaultProps = {
  modelExport: (f) => f,
  runtimeExport: (f) => f,
  fetchRecords: (f) => f,
  addOrEditRecord: (f) => f,
  onRecordClick: (f) => f,
  deleteRecord: (f) => f,
  cloneRecord: (f) => f,
  exportModel: (f) => f,
  data: [],
  totalRecords: 0,
  title: '',
  columnNames: {},
  activeRecord: {},
  classNames: {},
  tableType: '',
  currentPage: 0,
  permissions: {},
  exportLoader: {},
  showRedundantViews: false,
  runtime: false,
  clientHeight: 0,
};

TableView.propTypes = {
  modelExport: PropTypes.func,
  runtimeExport: PropTypes.func,
  fetchRecords: PropTypes.func,
  addOrEditRecord: PropTypes.func,
  onRecordClick: PropTypes.func,
  deleteRecord: PropTypes.func,
  cloneRecord: PropTypes.func,
  exportModel: PropTypes.func,
  data: PropTypes.instanceOf(Array),
  totalRecords: PropTypes.number,
  title: PropTypes.string,
  columnNames: PropTypes.instanceOf(Object),
  activeRecord: PropTypes.instanceOf(Object),
  classNames: PropTypes.instanceOf(Object),
  tableType: PropTypes.string,
  currentPage: PropTypes.number,
  permissions: PropTypes.instanceOf(Object),
  exportLoader: PropTypes.instanceOf(Object),
  showRedundantViews: PropTypes.bool,
  runtime: PropTypes.bool,
  clientHeight: PropTypes.number,
};

export default TableView;
